import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

import Button from "components/common/Button";
import SidePanel from "components/Screens/Marketplace/Customer/Shortlist/SidePanel";
import TileGrid from "components/Screens/Marketplace/Customer/Shortlist/TileGrid";
import TalentAvatar from "components/Screens/Marketplace/Customer/Shortlist/TalentAvatar";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { shouldShowAsk3, withAnswer, withAsk } from "components/Screens/Marketplace/Customer/Shortlist/preferenceAskStore";
import { andList, configTiles, preferenceSubtitle, preferenceTitle } from "components/Screens/Marketplace/Customer/utils/preferenceTiles";
import usePlgSignupConfig from "plg/screens/Signup/usePlgSignupConfig";
import { defaultWindows, fmtDay, nextWeekdays, SLOT_OPTIONS, timeZoneForState, toIsoDate } from "components/Screens/Marketplace/Customer/utils/availability";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import type { AvailabilitySlot, AvailabilityWindow, ShortlistItem } from "components/Screens/Marketplace/Customer/types";
import experienceLabel from "components/Screens/Marketplace/Customer/utils/experienceLabel";

const SEATS = 3;

/** One interview covers everyone shortlisted, in order, and at least three seats; an empty seat is Edge's to fill. */
function SeatCard({ item }: { item: ShortlistItem | null }) {
  if (!item) {
    return (
      <div className="sl-slot sl-slot--empty">
        <span className="sl-slot__ph"><AutoAwesomeOutlinedIcon fontSize="small" /></span>
        <div className="sl-slot__txt">
          <b>Edge will recommend someone</b>
          <span>Based on what you tell us below</span>
        </div>
      </div>
    );
  }
  const { talent } = item;
  return (
    <div className="sl-slot">
      <TalentAvatar talent={talent} size={40} />
      <div className="sl-slot__txt">
        <b>{talent.firstName}</b>
        <span>{experienceLabel(talent.totalExperience)}</span>
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const SEND_ERRORS: Record<string, string> = {
  EMPTY_SHORTLIST: "Your shortlist changed. Please review it and try again.",
  ALREADY_REQUESTED: "An interview was already requested for someone on your shortlist. Please review it and try again.",
};

function RequestInterviewsPanel({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { items, refresh, askState, updateAskState } = useShortlist();
  const api = useTalentApi();
  const customerState = api.customerState;
  const days = useMemo(() => nextWeekdays(10), [open]);
  const [windows, setWindows] = useState<AvailabilityWindow[]>(() => defaultWindows(days));
  const [note, setNote] = useState("");
  const { config } = usePlgSignupConfig();
  const ask3Tiles = useMemo(() => configTiles(config), [config]);
  const [picked, setPicked] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Everyone shortlisted is sent (no shortlist limit); fewer than three leaves seats for Edge to fill.
  const seats = Array.from({ length: Math.max(SEATS, items.length) }, (_, i) => items[i] || null);
  // Fewer than three shortlisted means the customer chose "Find them for me".
  const edgeFills = items.length < SEATS;
  const stated = askState.stated || [];
  const showAsk3 = !edgeFills && shouldShowAsk3(askState);
  const showTiles = edgeFills || showAsk3;
  const title = edgeFills ? "Let Edge fill the rest" : "Request interviews";

  useEffect(() => {
    if (!open) return;
    setWindows(defaultWindows(days));
    setNote("");
    // Edge needs to know what to look for; keep what they told us before.
    setPicked(edgeFills ? stated.map((t) => t.label) : []);
    setError(null);
    setDone(false);
  }, [open]);

  const names = andList(items.map((i) => i.talent.firstName));
  const tz = timeZoneForState(customerState);

  const setWindow = (i: number, patch: Partial<AvailabilityWindow>) =>
    setWindows((w) => w.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  // When Edge fills seats the question is required.
  const ok = !edgeFills || picked.length > 0;

  const send = async () => {
    if (sending || !ok) return;
    setSending(true);
    setError(null);
    const tiles = ask3Tiles.filter((t) => picked.includes(t.label)).map(({ cat, label }) => ({ cat, label }));
    const res = await api.createInterviewRound({
      availability: windows,
      note: note.trim() || undefined,
      preferenceTiles: tiles.length ? tiles : undefined,
      ...(edgeFills ? { letEdgeFill: true } : {}),
    });
    setSending(false);
    if (edgeFills) updateAskState(withAnswer(askState, tiles));
    else if (showAsk3) updateAskState(tiles.length ? withAnswer(askState, tiles) : withAsk(askState, 3, "dismissed"));
    if (res.ok === false) {
      await refresh();
      setError(SEND_ERRORS[res.code || ""] || "Something went wrong. Please try again.");
      return;
    }
    setDone(true);
    await refresh();
  };

  const toggle = (label: string) => setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));

  if (done) {
    return (
      <SidePanel open={open} title={title} onClose={onClose}>
        <div className="sl-done">
          <div className="sl-done__icon"><CheckIcon /></div>
          <h3>Request sent</h3>
          <p>
            {edgeFills
              ? "We are on it. We will find the others and set up all three interviews together."
              : "We are arranging your interviews. We will confirm each time with you once the candidate has agreed."}
          </p>
          <div className="sl-done__actions">
            <Button variant="contained" size="large" label="View interview status" onClick={() => { onClose(); navigate(api.routes.interviewStatus); }} />
            <Button variant="outlined" size="large" label="Continue browsing" onClick={() => { onClose(); navigate(api.routes.talentNetwork); }} />
          </div>
        </div>
      </SidePanel>
    );
  }

  return (
    <SidePanel
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          {/* In the footer, next to Send: the body scrolls, so an error at its top went unseen. */}
          {error && <div className="sl-panel__error" role="alert">{error}</div>}
          {!ok && <p className="sl-panel__hint sl-panel__hint--center">Pick at least one thing you are looking for to continue.</p>}
          <Button variant="contained" size="large" label="Send request" onClick={send} isDisabled={!ok} isLoading={sending} isFullWidth />
        </>
      }
    >
      <p className="sl-panel__lead">
        {edgeFills
          ? "Tell us what you are looking for and we will find the others. We will set up all three interviews together."
          : `We will arrange one interview session for ${names}, back to back. Edge handles the scheduling with the candidates.`}
      </p>
      <div className="sl-slots3">
        {seats.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key, no-underscore-dangle
          <SeatCard key={item ? item.talent._id : `open-${i}`} item={item} />
        ))}
      </div>
      {showTiles && ask3Tiles.length > 0 && (
        <section className={`sl-panel__sec${edgeFills ? "" : " sl-panel__sec--ask3"}`}>
          <h3>{preferenceTitle(config)}</h3>
          <p className="sl-panel__hint">{preferenceSubtitle(config, edgeFills ? "recommend" : "ask3")}</p>
          {edgeFills && stated.length > 0 && <p className="sl-panel__ack">We kept what you told us before. Add anything else that matters.</p>}
          <TileGrid tiles={ask3Tiles} selected={picked} onToggle={toggle} variant="rich" columns={2} />
        </section>
      )}
      <section className="sl-panel__sec">
        <h3>
          Anything else we should know? <span className="sl-panel__opt">Optional</span>
        </h3>
        <textarea
          className="sl-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Shift times, specialisations, anything that matters to you"
        />
      </section>
      <section className="sl-panel__sec">
        <h3>When are you free to interview?</h3>
        {windows.map((w, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="sl-window" key={i}>
            <span className="sl-window__n">{i + 1}</span>
            <select aria-label={`Day for window ${i + 1}`} className="sl-select" value={w.date} onChange={(e) => setWindow(i, { date: e.target.value })}>
              {days.map((d) => (
                <option key={toIsoDate(d)} value={toIsoDate(d)}>{fmtDay(d)}</option>
              ))}
            </select>
            <select aria-label={`Time for window ${i + 1}`} className="sl-select" value={w.slot} onChange={(e) => setWindow(i, { slot: e.target.value as AvailabilitySlot })}>
              {SLOT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        ))}
        <p className="sl-panel__hint">{`Times are in your local time zone (${tz}).`}</p>
      </section>
    </SidePanel>
  );
}

export default RequestInterviewsPanel;
